// HeroSection Component - Atmosphärischer Einstieg für die Homepage
// GoodLife: "Entdecke Orte mit Seele" - Emotionaler Einstieg mit Stimmungs-Schnellwahl
// GoodWork: "Entdecke Deinen GoodWork Place" - Arbeitsorte mit Charakter

import React, { useState, useEffect } from 'react';
import {
  Target,
  Users,
  Lightbulb,
  Wrench,
  GraduationCap,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { useRealm } from '../../context/RealmContext';

// Mood types for GoodLife
export type GoodLifeMoodType = 'calm' | 'cozy' | 'creative' | 'lively' | 'surprise';
// Mood types for GoodWork
export type GoodWorkMoodType = 'fokus' | 'austausch' | 'inspiration' | 'werkstatt' | 'wissen';
// Combined type
export type HeroMoodType = GoodLifeMoodType | GoodWorkMoodType;

interface MoodOption {
  id: HeroMoodType;
  icon?: React.ReactNode;
  emoji?: string;
  label: string;
  description: string;
}

// GoodLife Mood Options (unchanged)
const GOODLIFE_MOOD_OPTIONS: MoodOption[] = [
  { id: 'calm', emoji: '🌿', label: 'Ruhe', description: 'Stille Oasen' },
  { id: 'cozy', emoji: '☕', label: 'Gemütlich', description: 'Warme Orte' },
  { id: 'creative', emoji: '🎨', label: 'Kreativ', description: 'Inspiration finden' },
  { id: 'lively', emoji: '✨', label: 'Lebendig', description: 'Menschen treffen' },
  { id: 'surprise', emoji: '🎲', label: 'Überrasche mich', description: 'Zufall entscheidet' },
];

// GoodWork Mood Options (new)
const GOODWORK_MOOD_OPTIONS: MoodOption[] = [
  { id: 'fokus', icon: <Target className="w-5 h-5" />, label: 'Fokus', description: 'Konzentriert arbeiten' },
  { id: 'austausch', icon: <Users className="w-5 h-5" />, label: 'Austausch', description: 'Zusammen arbeiten' },
  { id: 'inspiration', icon: <Lightbulb className="w-5 h-5" />, label: 'Inspiration', description: 'Kreativ werden' },
  { id: 'werkstatt', icon: <Wrench className="w-5 h-5" />, label: 'Werkstatt', description: 'Etwas erschaffen' },
  { id: 'wissen', icon: <GraduationCap className="w-5 h-5" />, label: 'Wissen', description: 'Sich weiterentwickeln' },
];

// Hero background images for GoodLife - atmospheric scenes
const GOODLIFE_HERO_IMAGES = [
  'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1600&q=80', // Cozy café
  'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=1600&q=80', // People in warm light
  'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1600&q=80', // Garden café
  'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1600&q=80', // Coffee shop atmosphere
];

// Hero background images for GoodWork - productive spaces
const GOODWORK_HERO_IMAGES = [
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80', // Modern workspace
  'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80', // Team collaboration
  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&q=80', // Coworking space
  'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1600&q=80', // Creative office
];

interface HeroSectionProps {
  userName?: string;
  onMoodSelect: (mood: HeroMoodType) => void;
  onSkip?: () => void;
  onScrollDown?: () => void;
}

export function HeroSection({
  userName,
  onMoodSelect,
  onSkip,
  onScrollDown,
}: HeroSectionProps) {
  const { isGoodWork } = useRealm();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Select content based on realm
  const heroImages = isGoodWork ? GOODWORK_HERO_IMAGES : GOODLIFE_HERO_IMAGES;
  const moodOptions = isGoodWork ? GOODWORK_MOOD_OPTIONS : GOODLIFE_MOOD_OPTIONS;
  const headline = isGoodWork ? 'Entdecke Deinen GoodWork Place' : 'Entdecke Orte mit Seele';
  const subheadline = isGoodWork ? 'Was steht heute an?' : 'Wo möchtest du heute sein?';

  // Rotate background image every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  return (
    <section className="relative h-[70vh] min-h-[500px] max-h-[700px] w-full overflow-hidden">
      {/* Background Image with Crossfade */}
      <div className="absolute inset-0">
        {heroImages.map((img, index) => (
          <div
            key={img}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img}
              alt=""
              className="w-full h-full object-cover"
              onLoad={() => index === 0 && setImageLoaded(true)}
            />
          </div>
        ))}
      </div>

      {/* Gradient Overlays for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />

      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center px-4 text-center text-white">
        {/* Greeting */}
        {userName && (
          <p className="text-lg text-white/80 mb-2 animate-fade-in">
            {getGreeting()}, {userName.split(' ')[0]}
          </p>
        )}

        {/* Main Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 drop-shadow-lg animate-fade-in">
          {headline}
        </h1>

        {/* Subheadline */}
        <p className="text-xl sm:text-2xl text-white/90 mb-10 max-w-2xl drop-shadow animate-fade-in font-light">
          {subheadline}
        </p>

        {/* Mood Quick Selection */}
        <div className="flex flex-wrap justify-center gap-3 mb-8 animate-slide-up">
          {moodOptions.map((mood) => (
            <button
              key={mood.id}
              onClick={() => onMoodSelect(mood.id)}
              className={`group flex items-center gap-2 px-5 py-3
                         bg-white/15 backdrop-blur-md border border-white/30
                         rounded-full text-white font-medium
                         hover:bg-white/25 hover:border-white/50 hover:scale-105
                         transition-all duration-300 shadow-lg ${
                           isGoodWork ? 'hover:border-indigo-300' : ''
                         }`}
            >
              {mood.icon ? (
                <span className="opacity-90">{mood.icon}</span>
              ) : (
                <span className="text-xl">{mood.emoji}</span>
              )}
              <span>{mood.label}</span>
            </button>
          ))}
        </div>

        {/* Skip Link */}
        {onSkip && (
          <button
            onClick={onSkip}
            className="text-white/70 hover:text-white text-sm underline underline-offset-4
                       transition-colors duration-200"
          >
            Alle Orte zeigen
          </button>
        )}

        {/* Scroll Down Indicator */}
        {onScrollDown && (
          <button
            onClick={onScrollDown}
            className="absolute bottom-8 left-1/2 -translate-x-1/2
                       text-white/60 hover:text-white transition-colors
                       animate-bounce"
            aria-label="Nach unten scrollen"
          >
            <ChevronDown className="w-8 h-8" />
          </button>
        )}
      </div>

      {/* Image Indicators */}
      <div className="absolute bottom-4 right-4 flex gap-1.5">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentImageIndex
                ? 'bg-white w-4'
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Bild ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default HeroSection;
